import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, tokenUtils } from '../services/api';
import { API_ENDPOINTS, QUERY_KEYS } from '../utils/constants';
import { showErrorToast, showSuccessToast } from '../utils/toast';

// Signup mutation
export const useSignup = () => {
  return useMutation({
    mutationFn: async (userData) => {
      const response = await apiClient.post(API_ENDPOINTS.USERS.SIGNUP, userData);
      return response;
    },
    onSuccess: () => {
      showSuccessToast('Account created successfully! Please check your email for verification code.');
    },
    onError: (error) => {
      showErrorToast(error, 'Signup failed. Please check your information and try again.');
    },
  });
};

// Verify email mutation
export const useVerifyEmail = () => {
  return useMutation({
    mutationFn: async ({ email, code }) => {
      const response = await apiClient.post(API_ENDPOINTS.USERS.VERIFY_EMAIL, {
        email,
        code,
      });
      return response;
    },
    // Toast removed - using embedded alert in component instead
    onError: (error) => {
      showErrorToast(error, 'Email verification failed. Please check your code and try again.');
    },
  });
};

// Resend verification code mutation
export const useResendVerificationCode = () => {
  return useMutation({
    mutationFn: async (email) => {
      const response = await apiClient.post(API_ENDPOINTS.USERS.RESEND_VERIFICATION, {
        email,
      });
      return response;
    },
    // Toast removed - using embedded alert in component instead
    onError: (error) => {
      showErrorToast(error, 'Failed to resend verification code. Please try again.');
    },
  });
};

// Get current user query
export const useCurrentUser = (options = {}) => {
  return useQuery({
    queryKey: QUERY_KEYS.USERS.ME,
    queryFn: async () => {
      const response = await apiClient.get(API_ENDPOINTS.USERS.ME);
      return response;
    },
    enabled: tokenUtils.hasTokens() && (options.enabled !== false),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on 401/403 errors
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 1;
    },
    onError: (error) => {
      // Only show toast for non-auth errors (401/403 are handled silently)
      if (error?.status !== 401 && error?.status !== 403) {
        showErrorToast(error, 'Failed to load user profile.');
      }
    },
    ...options,
  });
};

// Update profile mutation
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userData) => {
      const response = await apiClient.patch(API_ENDPOINTS.USERS.UPDATE_ME, userData);
      return response;
    },
    onSuccess: (data) => {
      // Update user in localStorage
      if (data) {
        localStorage.setItem('user', JSON.stringify(data));
      }
      // Invalidate and refetch user queries
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.USERS.ME });
      queryClient.setQueryData(QUERY_KEYS.USERS.ME, data);
      showSuccessToast('Profile updated successfully!');
    },
    onError: (error) => {
      showErrorToast(error, 'Failed to update profile. Please try again.');
    },
  });
};

// Accept contract mutation
export const useAcceptContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      const response = await apiClient.post(API_ENDPOINTS.CONTRACTS.ACCEPT, payload);
      return response;
    },
    onSuccess: (data) => {
      if (data) {
        localStorage.setItem('user', JSON.stringify(data));
      }
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.USERS.ME });
      queryClient.setQueryData(QUERY_KEYS.USERS.ME, data);
    },
    onError: (error) => {
      showErrorToast(error, 'Failed to save contract acceptance.');
    }
  });
};

// Get active contract for current user role
export const useActiveContract = (options = {}) => {
  return useQuery({
    queryKey: ['contracts', 'active'],
    queryFn: async () => {
      const response = await apiClient.get(API_ENDPOINTS.CONTRACTS.ACTIVE);
      return response;
    },
    enabled: tokenUtils.hasTokens() && (options.enabled !== false),
    staleTime: 2 * 60 * 1000,
    ...options,
  });
};

// Get user by ID query
export const useUser = (userId, options = {}) => {
  return useQuery({
    queryKey: QUERY_KEYS.USERS.BY_ID(userId),
    queryFn: async () => {
      const response = await apiClient.get(API_ENDPOINTS.USERS.BY_ID(userId));
      return response;
    },
    enabled: !!userId && (options.enabled !== false),
    ...options,
  });
};

// Get user roles query
export const useUserRoles = (options = {}) => {
  return useQuery({
    queryKey: QUERY_KEYS.USER_ROLES.LIST,
    queryFn: async () => {
      const response = await apiClient.get(API_ENDPOINTS.USER_ROLES.LIST);
      return response;
    },
    ...options,
  });
};

// Get job roles query (public endpoint, no auth required)
export const useJobRoles = (options = {}) => {
  return useQuery({
    queryKey: QUERY_KEYS.JOB_ROLES.LIST,
    queryFn: async () => {
      const response = await apiClient.get(API_ENDPOINTS.JOB_ROLES.LIST);
      return response;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - job roles don't change often
    ...options,
  });
};

// Get required documents query
export const useRequiredDocuments = (options = {}) => {
  return useQuery({
    queryKey: QUERY_KEYS.USERS.REQUIRED_DOCUMENTS,
    queryFn: async () => {
      const response = await apiClient.get(API_ENDPOINTS.USERS.REQUIRED_DOCUMENTS);
      return response;
    },
    enabled: tokenUtils.hasTokens() && (options.enabled !== false),
    staleTime: 5 * 60 * 1000, // 5 minutes
    onError: (error) => {
      if (error?.status !== 401 && error?.status !== 403) {
        showErrorToast(error, 'Failed to load required documents.');
      }
    },
    ...options,
  });
};

// Get user's uploaded documents query
export const useMyDocuments = (options = {}) => {
  return useQuery({
    queryKey: QUERY_KEYS.USERS.MY_DOCUMENTS,
    queryFn: async () => {
      const response = await apiClient.get(API_ENDPOINTS.USERS.MY_DOCUMENTS);
      return response;
    },
    enabled: tokenUtils.hasTokens() && (options.enabled !== false),
    staleTime: 2 * 60 * 1000, // 2 minutes
    onError: (error) => {
      if (error?.status !== 401 && error?.status !== 403) {
        showErrorToast(error, 'Failed to load documents.');
      }
    },
    ...options,
  });
};

// Upload document mutation (bulk upload support)
export const useUploadDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ documents }) => {
      // documents should be an array of { document_type_id, file }
      const formData = new FormData();
      
      // Backend expects: documents[0][document_type_id], documents[0][file], etc.
      documents.forEach((doc, index) => {
        formData.append(`documents[${index}][document_type_id]`, doc.document_type_id);
        formData.append(`documents[${index}][file]`, doc.file);
      });

      // Use fetch directly for FormData with multipart/form-data
      const token = tokenUtils.getAccessToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
      const response = await fetch(
        `${apiUrl}${API_ENDPOINTS.USERS.UPLOAD_DOCUMENT}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            // Don't set Content-Type header - browser will set it with boundary
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
        throw { status: response.status, data: error };
      }

      return await response.json();
    },
    onSuccess: (data) => {
      // Invalidate queries to refetch
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.USERS.REQUIRED_DOCUMENTS });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.USERS.MY_DOCUMENTS });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.USERS.ME });
      
      // Show success message
      const uploadedCount = data?.uploaded?.length || 0;
      if (uploadedCount > 0) {
        showSuccessToast(`${uploadedCount} document(s) uploaded successfully!`);
      }
      
      // Show errors if any
      if (data?.errors && data.errors.length > 0) {
        data.errors.forEach(error => {
          showErrorToast({ data: { detail: error } }, error);
        });
      }
    },
    onError: (error) => {
      // Extract specific error messages from the response
      if (error?.data?.errors && Array.isArray(error.data.errors) && error.data.errors.length > 0) {
        // Show each specific error message
        error.data.errors.forEach((errorMsg) => {
          showErrorToast(errorMsg, 'Failed to upload document(s).');
        });
      } else if (error?.data?.detail) {
        // Show the detail message if no specific errors array
        showErrorToast(error, error.data.detail);
      } else {
        // Fallback to generic message
        showErrorToast(error, 'Failed to upload document(s). Please try again.');
      }
    },
  });
};

// Legacy hook for backward compatibility (single document upload)
export const useUserDocuments = (options = {}) => {
  return useMyDocuments(options);
};

